import { isBefore } from 'date-fns';
import { Dispatch } from 'redux';

import ReconnectingWebSocket from 'partysocket/ws';

import { serializeDossierFromAPI } from '../../resources/dossier/serializers';
import { AllActions } from '../redux/types';
import { sendErrorReport } from '../utilities/sendErrorReport';
import { deleteDossierInCache, updateDossierInCache } from '../../resources/dossier/cache-utils';
import { getDossierOverviewFromCache } from '../../resources/dossier-overview/cache-utils';
import { DossierId, SerializedDossier } from '../../resources/dossier/types';
import { queryClient } from '../providers/react-query-provider';
import { getDiscussionMessagesQueryKey } from '../../resources/discussion/queries';

export const attachDossierSocketListeners = (
    _dispatch: Dispatch<AllActions>,
    socket: ReconnectingWebSocket,
) => {
    if (typeof window === 'undefined') return () => {};

    const onMessage = (event: MessageEvent) => {
        if (!event.data) {
            return;
        }

        if (typeof event.data === 'string') {
            try {
                handleWebSocketDossierMessages(event.data);
            } catch (e) {
                sendErrorReport(e as Error);
            }
        }
    };

    socket.addEventListener('message', onMessage);

    return () => {
        socket.removeEventListener('message', onMessage);
    };
};

// Helper function to parse and route WebSocket messages - exported for testing
export const handleWebSocketDossierMessages = (messageData: string): void => {
    const response = JSON.parse(messageData);
    const messageType = response.data.type;

    if (messageType === 'dossier-overview-updated') {
        const dossierId = response.data.id;
        const dossierFromSocket = serializeDossierFromAPI(response);
        updateDossierInRealTime(dossierId, dossierFromSocket);
        return;
    }

    if (messageType === 'dossier-overview-deleted') {
        const { id } = response.data;
        deleteDossierInCache(id);
        return;
    }

    // * For now we decided to stop reading the memo from the socket, as it is not handled by the
    // Memo component
    // if (messageType === 'dossier-memo') {
    //     const memo = memoSerializer(response.data);

    //     // Here both calls need to be kepts until the memo api calls are migrated to react-query
    //     updateDossierMemoInCache(response.data.id, memo);
    //     dispatch(changeMemoSuccess(response.data.id, memo));
    // }
};

export const updateDossierInRealTime = (
    dossierId: DossierId,
    dossierFromSocket: SerializedDossier,
) => {
    const dossierFromCache = getDossierOverviewFromCache(dossierId);

    // Refresh discussion when we detect a new traveler message
    if (
        dossierFromCache &&
        checkMessageUpdate(
            dossierFromCache?.discussion?.lastTravellerToAgentMessageDate,
            dossierFromSocket.dossier[dossierId]?.discussion?.lastTravellerToAgentMessageDate,
        )
    ) {
        void queryClient.invalidateQueries({ queryKey: getDiscussionMessagesQueryKey(dossierId) });
    }

    // Important: Let this update at the end of function because we must check before the difference between what we have in cache vs what we got from Socket
    updateDossierInCache(dossierFromSocket);
};

export const checkMessageUpdate = (
    cachedDate: Date | null | undefined,
    newDate: Date | null | undefined,
): boolean => {
    if (!newDate) return false;

    // first message
    if (!cachedDate) return true;

    // newer message
    return isBefore(cachedDate, newDate);
};
